import React, { useState } from 'react';
import { IconButton, Select, MenuItem, Popover, Typography, Box, Modal, CircularProgress, Tooltip } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import { DataGrid, GridOverlay, getGridStringOperators } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Circle as CircleIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Cake as CakeIcon,
  WorkHistory as WorkHistoryIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useLoader } from '../../context/LoaderContext.js';
import { getAppsignaturePreviewByIdUser } from '../../api/signatureManagementApi.js'
import customLocaleText from '../../constants/DataGridLocaleTextConstants.js';
import { useAuth } from '../../context/AuthContext.js';
import features from '../../config/features.js';

function CustomLoadingOverlay() {
  return (
    <GridOverlay>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 4,
          my: 'auto',
        }}
      >
        <CircularProgress size={44} thickness={4} color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mt: 1 }}>
          Cargando cuentas de dominio...
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 450 }}>
          Obteniendo las cuentas registradas del dominio. Por favor, espere un momento.
        </Typography>
      </Box>
    </GridOverlay>
  );
}

function UsersDataGrid({
  users,
  templates,
  handleOpenDeleteModal,
  handleOpenModal,
  handleOpenSyncModal,
  onMassUpdate,
  onSelectionChange,
  loading = false,
  paginationMode = "server",
  rowCount,
  paginationModel: externalPaginationModel,
  onPaginationModelChange,
  filterMode,
  filterModel,
  onFilterModelChange
}) {
  const resolvedFilterMode = filterMode || (onFilterModelChange ? "server" : "client");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const [selectedRows, setSelectedRows] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const { user } = useAuth();

  const [internalPaginationModel, setInternalPaginationModel] = useState({ page: 0, pageSize: 10 });
  const paginationModel = externalPaginationModel !== undefined ? externalPaginationModel : internalPaginationModel;

  const handlePaginationChange = (newModel) => {
    if (onPaginationModelChange) {
      onPaginationModelChange(newModel);
    } else {
      setInternalPaginationModel(newModel);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  const handleOpenPreviewModal = async (userId) => {
    showLoader();
    getAppsignaturePreviewByIdUser(userId)
      .then(response => {
        setSelectedPreviewTemplate(response.data.signaturePreview.template_html);
      })
      .finally(() => hideLoader())
      .catch(error => {
        setOpenSnackbar({ open: true, text: 'Hubo un error al cargar la firma de usuario', error: true });
        console.error('Error fetching templates:', error);
      });
  };

  const handleClosePreviewModal = () => {
    setSelectedPreviewTemplate(null);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const getDateFromTimestamp = (timestamp) => {
    if (!timestamp) return 'Fecha inválida';
    return moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss');
  };

  // ============================================
  // ✅ FUNCIÓN CORREGIDA PARA MANEJAR TIMESTAMPS
  // ============================================
  const formatSimpleDate = (dateValue) => {
    if (!dateValue || dateValue === '') {
      return null;
    }

    // Si es un objeto con _seconds (Timestamp serializado de Firestore)
    if (dateValue._seconds) {
      return moment.unix(dateValue._seconds).format('DD-MM-YYYY');
    }

    // Si es un objeto con seconds (algunas versiones de Firestore)
    if (dateValue.seconds) {
      return moment.unix(dateValue.seconds).format('DD-MM-YYYY');
    }

    // Si tiene método toDate (Timestamp de Firestore no serializado)
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return moment(dateValue.toDate()).format('DD-MM-YYYY');
    }

    // Si es un string, intentar parsearlo
    if (typeof dateValue === 'string') {
      const date = moment(dateValue);
      if (date.isValid()) {
        return date.format('DD-MM-YYYY');
      }
    }

    // Si es un número (timestamp Unix en segundos)
    if (typeof dateValue === 'number') {
      // Si el número es muy pequeño, probablemente es inválido
      if (dateValue < 100000) {
        return null;
      }
      // Si es mayor a 10000000000, es timestamp en milisegundos
      if (dateValue > 10000000000) {
        return moment(dateValue).format('DD-MM-YYYY');
      }
      // Si no, es timestamp en segundos
      return moment.unix(dateValue).format('DD-MM-YYYY');
    }

    return null;
  };

  const getTemplateNameById = (id) => {
    const template = templates.find(t => t.id === id)
    return template?.template_name || id
  }

  // Manejar selección de filas
  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
    // Notificar al componente padre sobre la selección
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  const customFilterOperators = [
    {
      label: 'Activo/Inactivo',
      value: 'isActiveFilter',
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        const isActive = filterItem.value === 'Activo';
        return (row) => (row === isActive);
      },
      InputComponent: ({ item, applyValue }) => (
        <Select
          value={item.value || ''}
          onChange={(event) => applyValue({ ...item, value: event.target.value })}
          style={{ width: '100%', marginTop: 'auto' }}
          data-testid="customStatusSelect"
          id="customStatusSelect"
        >
          <MenuItem value="">
            <em>--Seleccione--</em>
          </MenuItem>
          <MenuItem value={"Activo"}>Activo</MenuItem>
          <MenuItem value={"Inactivo"}>Inactivo</MenuItem>
        </Select>
      ),
    },
  ];

  const emailFilterOperators = getGridStringOperators()
    .filter(op => op.value === 'startsWith' || op.value === 'equals')
    .sort((a, b) => (a.value === 'startsWith' ? -1 : 1));

  const specialDateColumns = [
    {
      field: 'birthday',
      headerName: '🎂 Cumpleaños',
      flex: 0.12,
      filterable: false,
      renderCell: (params) => {
        const formattedDate = formatSimpleDate(params.row.birthday);

        if (formattedDate) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
                gap: 0.5,
                color: 'primary.main'
              }}
            >
              <CakeIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                {formattedDate}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic'
              }}
            >
              Dato pendiente
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'aniversary',
      headerName: '🎉 Aniversario',
      flex: 0.12,
      filterable: false,
      renderCell: (params) => {
        const formattedDate = formatSimpleDate(params.row.aniversary);

        if (formattedDate) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
                gap: 0.5,
                color: 'secondary.main'
              }}
            >
              <WorkHistoryIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                {formattedDate}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic'
              }}
            >
              Dato pendiente
            </Typography>
          </Box>
        );
      },
    },
  ];

  const columns = [
    {
      display: 'flex',
      field: 'is_active',
      headerName: 'Estado',
      flex: 0.08,
      filterable: true,
      filterOperators: customFilterOperators,
      renderCell: (params) => (
        <CircleIcon
          sx={{
            color: params.row.is_active ? 'green' : 'grey',
          }}
        />
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 0.25,
      filterable: true,
      filterOperators: emailFilterOperators,
    },
    {
      field: 'signature_template_id',
      headerName: 'Template',
      flex: 0.18,
      filterable: false,
      valueGetter: (param) => getTemplateNameById(param)
    },
    {
      field: 'created_at',
      headerName: 'Fecha de creación',
      valueGetter: (params) => getDateFromTimestamp(params?._seconds),
      flex: 0.15,
      filterable: false,
    },
    ...(features.specialTemplatesEnabled ? specialDateColumns : []),
    {
      filterable: false,
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.1,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => {
        const htmlContent = params.row.template_html || '';
        return (<>
          <IconButton
            onClick={() => handleOpenPreviewModal(params.row.id)}
            aria-label="preview"
            title='Vista previa'
          >
            <VisibilityIcon />
          </IconButton>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ padding: 2 }}>
              <Typography variant="caption">Vista previa:</Typography>
              <Box
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                sx={{
                  padding: 1,
                  border: '1px solid #ddd',
                  background: '#fff',
                  maxWidth: '200px',
                  maxHeight: '150px',
                  overflow: 'hidden',
                }}
              />
            </Box>
          </Popover>
          {user?.isAdmin && handleOpenSyncModal ? (
            <Tooltip title={params.row.is_active ? "Actualizar firma en Gmail" : "Usuario inactivo"}>
              <span>
                <IconButton
                  onClick={() => handleOpenSyncModal(params.row)}
                  aria-label="sync-signature"
                  disabled={!params.row.is_active}
                  color="primary"
                >
                  <SyncIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {user?.isAdmin ? <><IconButton onClick={() => handleOpenModal(params.row)} aria-label="edit" data-testid="EditIcon">
            <EditIcon />
          </IconButton>
            <IconButton onClick={() => handleOpenDeleteModal(params.row)} aria-label="delete" data-testid="DeleteIcon">
              <DeleteIcon />
            </IconButton></> : null}
        </>)
      }
    }
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '480px',
        maxHeight: 'calc(100vh - 280px)',
      }}
    >
      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        components={{
          LoadingOverlay: CustomLoadingOverlay,
        }}
        paginationMode={paginationMode}
        filterMode={resolvedFilterMode}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        rowCount={rowCount !== undefined ? rowCount : users.length}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[5, 10, 15, 20]}
        checkboxSelection={user?.isAdmin}
        sx={{
          border: 0,
          minHeight: 450,
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 350,
          },
          '& .MuiDataGrid-overlayWrapperInner': {
            minHeight: 350,
          },
        }}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        localeText={customLocaleText}
      />

      {selectedPreviewTemplate && (
        <Modal
          open={selectedPreviewTemplate}
          onClose={handleClosePreviewModal}
          aria-labelledby="preview-modal-title"
          aria-describedby="preview-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <Typography id="preview-modal-title" variant="h6" sx={{ mb: 2 }}>
              Vista Previa de la firma
            </Typography>
            <Box
              id="preview-modal-description"
              dangerouslySetInnerHTML={{ __html: selectedPreviewTemplate }}
              sx={{
                border: '1px solid #ddd',
                padding: 2,
                overflow: 'auto',
                maxWidth: '800px',
                width: 'auto',
                maxHeight: '100%',
              }}
            />
          </Box>
        </Modal>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default UsersDataGrid;